<script setup lang="ts">
import { ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";

// Brand and Home are plain <a href> on purpose — `/` is still served by
// the legacy public/index.html. Using RouterLink there would intercept
// the click client-side and render an empty SPA home instead. Only routes
// the SPA actually owns (/products, /products/:slug) use RouterLink.

const isMenuOpen = ref(false);
const route = useRoute();

function closeMenu() {
  isMenuOpen.value = false;
}

// Close the mobile drawer whenever navigation completes (covers link clicks,
// browser back/forward, programmatic router.push).
watch(() => route.fullPath, closeMenu);
</script>

<template>
  <header class="site-header">
    <div class="container header-row">
      <a href="/" class="brand" @click="closeMenu">
        <img
          src="/assets/img/logo.png"
          alt="RanaVolt"
          height="36"
          width="120"
        />
      </a>

      <button
        type="button"
        class="menu-toggle"
        :aria-expanded="isMenuOpen"
        aria-controls="primary-nav"
        aria-label="Toggle menu"
        @click="isMenuOpen = !isMenuOpen"
      >
        <svg
          v-if="!isMenuOpen"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>

      <nav
        id="primary-nav"
        class="nav"
        :class="{ open: isMenuOpen }"
        aria-label="Primary"
      >
        <a href="/" @click="closeMenu">Home</a>
        <RouterLink to="/products" active-class="active" @click="closeMenu"
          >Products</RouterLink
        >
        <a href="/configurator" @click="closeMenu">Configure</a>
        <a href="/about" @click="closeMenu">About</a>
        <a href="/faq" @click="closeMenu">FAQ</a>
        <a href="/contact" @click="closeMenu">Contact</a>
        <a href="/contact" class="nav-cta" @click="closeMenu">Get a Quote</a>
      </nav>
    </div>
  </header>
</template>

<style scoped>
.site-header {
  background: var(--rv-paper);
  border-bottom: 1px solid var(--rv-border);
  position: sticky;
  top: 0;
  z-index: var(--rv-z-sticky);
}
.header-row {
  display: flex;
  align-items: center;
  gap: var(--rv-space-6);
  padding: var(--rv-space-4) 0;
  position: relative; /* anchor for the absolute mobile nav drawer */
}
.brand {
  display: inline-flex;
}
.brand img {
  height: 36px;
  width: auto;
}

/* Hamburger button — hidden on desktop, shown when the nav can't fit. */
.menu-toggle {
  display: none;
  margin-left: auto;
  background: none;
  border: 0;
  padding: var(--rv-space-2);
  color: var(--rv-ink);
  cursor: pointer;
  line-height: 0;
  border-radius: var(--rv-radius-sm);
}
.menu-toggle:hover {
  color: var(--rv-green);
}
.menu-toggle:focus-visible {
  outline: 2px solid var(--rv-green);
  outline-offset: 2px;
}

.nav {
  margin-left: auto;
  display: flex;
  gap: var(--rv-space-5);
  align-items: center;
}
.nav a {
  color: var(--rv-ink-soft);
  font-weight: 500;
  font-size: 0.95rem;
}
.nav a:hover,
.nav a.active {
  color: var(--rv-green);
}
.nav-cta {
  background: var(--rv-orange);
  color: #fff !important;
  padding: 8px 16px;
  border-radius: var(--rv-radius-full);
  font-weight: 600;
}
.nav-cta:hover {
  background: #b47338;
}

/* ── Mobile: collapse nav into a drawer below the header ───────────────
   Breakpoint matches the legacy site (860px) so both behave identically. */
@media (max-width: 860px) {
  .menu-toggle {
    display: inline-flex;
  }

  .nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    margin: 0;
    padding: var(--rv-space-3) var(--rv-space-6) var(--rv-space-5);
    background: var(--rv-paper);
    border-bottom: 1px solid var(--rv-border);
    box-shadow: var(--rv-shadow-md);
  }
  .nav.open {
    display: flex;
  }

  .nav a {
    padding: var(--rv-space-3) 0;
    border-bottom: 1px solid var(--rv-border);
    font-size: 1rem;
  }
  .nav a:last-child {
    border-bottom: 0;
  }

  .nav-cta {
    margin-top: var(--rv-space-3);
    text-align: center;
    border-bottom: 0 !important;
  }
}
</style>
